import { Icon } from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { STRATEGIC_VISION_MAX_LENGTH } from './useStrategicVisionDraft';
import { TranslationField } from '../TranslationField';
import { TranslationLanguagePicker } from '../TranslationField/TranslationLanguagePicker';

import type { LocalizedText } from '@/utils/localization';

interface StrategicVisionTranslationsTabProps {
    /** The full draft dictionary, current (reference) language included. */
    vision: LocalizedText;
    configuredLanguages: string[];
    currentLanguage: string;
    /** Called with the merged dictionary on every keystroke — the caller owns the draft. */
    onChange: (vision: LocalizedText) => void;
}

/**
 * "Traductions" tab of the strategic vision editor, same shape as `ValueTranslationsTab`
 * (language picker, one field per selected language, completeness badge) but reduced to the
 * vision's single field.
 */
export default function StrategicVisionTranslationsTab({
    vision,
    configuredLanguages,
    currentLanguage: editingLanguage,
    onChange,
}: StrategicVisionTranslationsTabProps) {
    const { t } = useTranslation();
    const additionalLanguages = useMemo(
        () => configuredLanguages.filter((lang) => lang !== editingLanguage),
        [configuredLanguages, editingLanguage],
    );

    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
        additionalLanguages[0] ?? null,
    );

    useEffect(() => {
        const selectionStillValid =
            !!selectedLanguage && additionalLanguages.includes(selectedLanguage);

        if (!selectionStillValid && additionalLanguages.length > 0) {
            setSelectedLanguage(additionalLanguages[0]);
        }
    }, [additionalLanguages, selectedLanguage]);

    if (additionalLanguages.length === 0) {
        return (
            <div className={'flex flex-col items-center justify-center py-12 text-gray-500'}>
                <Icon
                    name={'location-global'}
                    size={'lg'}
                    colorClassName={'text-gray-400'}
                />

                <p className={'mt-4 text-lg font-medium'}>
                    {t('visionValues.noAdditionalLanguages')}
                </p>

                <p className={'text-[14px] text-gray-500'}>
                    {t('visionValues.addEvaluationLanguagesHint')}
                </p>
            </div>
        );
    }

    const currentLanguage = selectedLanguage || additionalLanguages[0];

    if (!selectedLanguage) {
        return null;
    }

    return (
        <TranslationLanguagePicker
            languages={additionalLanguages}
            selectedLanguage={currentLanguage}
            onSelectLanguage={setSelectedLanguage}
            isLanguageComplete={(language) => !!vision[language]}
        >
            <TranslationField
                label={t('strategicVision.visionLabel')}
                value={vision[currentLanguage] ?? ''}
                onChange={(text) => onChange({ ...vision, [currentLanguage]: text })}
                maxLength={STRATEGIC_VISION_MAX_LENGTH}
                placeholder={t('strategicVision.placeholder')}
                overLimitMessage={t('strategicVision.maxLength', {
                    max: STRATEGIC_VISION_MAX_LENGTH,
                })}
                missingTranslationLabel={t('visionValues.missingTranslation')}
                multiline
                dataTestId={`strategic-vision-translation-${currentLanguage}`}
            />
        </TranslationLanguagePicker>
    );
}
